/**
 * React mount for the worker-hosted sync path (R8 file 4). Boots the thin tab
 * Repo (tab-repo.ts) and provides it on the STOCK automerge RepoContext, plus
 * the shared-repo controls on TabRepoBootContext.
 *
 * Lives in contexts/ beside its predecessor: at end-state this IS the app's
 * root repo provider, replacing ./RepoContext.tsx when the last surface
 * migrates. The non-React sync boot layer stays in lib/sync/.
 *
 * COEXISTENCE BY SHADOWING (the strangler, in JSX): the legacy tree mounts the
 * stock RepoContext with the main-thread repo (./RepoContext.tsx). This
 * provider is wrapped AROUND individual migrated surfaces, where React context
 * nesting shadows the legacy value locally — migrated surfaces read through the shared repo, everything else keeps reading the legacy repo, no flag, no global
 * switch. When the last surface migrates, the legacy provider (and engine)
 * deletes.
 *
 * Children render only once the shared-repo handshake completes (rendering against
 * the legacy repo first and switching would double-load docs across repos).
 */

import { RepoContext } from '@automerge/automerge-repo-react-hooks'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { createTabRepo, type TabRepoBoot } from './tab-repo'

const TabRepoBootContext = createContext<TabRepoBoot | null>(null)

/** Shared-repo controls for migrated surfaces. */
export function useTabRepoBoot(): TabRepoBoot {
  const boot = useContext(TabRepoBootContext)
  if (!boot) {
    throw new Error('useTabRepoBoot requires an ancestor <TabRepoProvider>')
  }
  return boot
}

/**
 * The same boot, or null where there is no provider.
 *
 * For a component that can render WITHOUT a repo and simply do less: an
 * attachment falls back to a named chip rather than an image (RFC 0288). The
 * throwing version above stays the default, because a surface that needs a repo
 * and silently has none is a bug worth failing loudly on.
 */
export function useOptionalTabRepoBoot(): TabRepoBoot | null {
  return useContext(TabRepoBootContext)
}

export function TabRepoProvider({
  children,
  fallback = null,
  relay,
  defaultLocalOnly = false,
}: {
  children: ReactNode
  fallback?: ReactNode
  /**
   * Point this surface's shared repo at a SPECIFIC server (e.g. the new-stack
   * project surface → the subduction server), overriding the legacy
   * `VITE_SYNC_SERVER_URL` default. A `MOD_WORKER_SYNC_RELAY` dev override still
   * wins over it.
   */
  relay?: string
  /**
   * When true, an UNSET `MOD_WORKER_SYNC_RELAY` means local-only (no server)
   * instead of the prod default — so a self-contained surface like `/local`
   * works in a fresh browser / incognito with zero setup and no ticket wait. An
   * explicit flag value still wins (set it to a URL to go networked).
   */
  defaultLocalOnly?: boolean
}) {
  const [boot, setBoot] = useState<TabRepoBoot | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let mounted = true
    void createTabRepo({ relay, defaultLocalOnly }).then(
      (b) => {
        if (mounted) setBoot(b)
      },
      (cause) => {
        const reason = cause instanceof Error ? cause : new Error(String(cause))
        console.error('[mod repo] boot failed:', reason)
        if (mounted) setError(reason)
      },
    )
    return () => {
      mounted = false
    }
  }, [relay, defaultLocalOnly])

  if (error)
    return (
      <div
        role="alert"
        className="flex h-screen flex-col items-center justify-center gap-3 text-sm"
      >
        <p>Could not connect to Mod: {error.message}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    )
  if (!boot) return <>{fallback}</>
  return (
    <TabRepoBootContext.Provider value={boot}>
      <RepoContext.Provider value={boot.repo}>{children}</RepoContext.Provider>
    </TabRepoBootContext.Provider>
  )
}
